import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { addData, deleteData, loadData, updateData } from "../../../../config/data";
import EmptyAsset from "../emptyAsset";
import Lang from "../../../../config/lang";
import { useProfileTab } from "../../ProfileTabContext";

const EducationForm = ({ setIsFading, updatePercentage, profileCompletion }) => {
  const { lang } = useSelector((state) => state.globalReducer);
  const { currentTabIndex, setCurrentTabIndex } = useProfileTab();
  const applicantID = localStorage?.getItem("applicantID");
  const userToken = localStorage?.getItem('authToken') || '';
  const [isEdit, setisEdit] = useState(false);
  const [educations, setEducations] = useState([]);
  const [isLoading, setisLoading] = useState('');

  // 🔹 Load data from API
  const loadJobata = async () => {
    loadData({
      url: `EducationHistory`,
      params: [{ title: "filter", value: `applicant:${applicantID}` }],
      userToken,
    }).then(async (res) => {
      if (res?.data) {
        const resData = res.data;
        const sortedData = [...resData].sort(
          (a, b) => new Date(b.startDate) - new Date(a.startDate)
        );
        setEducations(sortedData);
      }
    });
  };

  useEffect(() => {
    loadJobata();
  }, []);

  // 🔹 Handle field changes
  const handleChange = (index, field, value) => {
    const updated = [...educations];
    updated[index][field] = value;
    setEducations(updated);
  };

  const isFormReady = () => {
    const hasEmptyId = educations.some(item => !item.educationLevel || !item.institutionName || !item.major || !item.startDate || !item.gpa);
    return !hasEmptyId;
  }

  const isFormempty = () => {
    return educations.length === 0;
  }

  // 🔹 Add new empty row
  const handleAdd = () => {
    setisEdit(true)
    setEducations([
      ...educations,
      {
        educationLevel: "",
        institutionName: "",
        institutionLocation: "",
        major: "",
        startDate: "",
        endDate: "",
        gpa: "",
      },
    ]);
  };

  const saveEducation = async (education) => {
    try {
      if (education.educationID) {

        await updateData({
          url: "EducationHistory",
          userToken,
          reqBody: {
            educationID: education.educationID || undefined, // only send if exists
            applicantID: applicantID,
            educationLevel: education.educationLevel,
            institutionName: education.institutionName,
            institutionLocation: education.institutionLocation,
            major: education.major,
            startDate: education.startDate,
            endDate: education.endDate,
            gpa: education.gpa,
          }
        })
      } else {
        await addData({
          url: "EducationHistory",
          userToken,
          reqBody: {
            educationID: education.educationID || undefined, // only send if exists
            applicantID: applicantID,
            educationLevel: education.educationLevel,
            institutionName: education.institutionName,
            institutionLocation: education.institutionLocation,
            major: education.major,
            startDate: education.startDate,
            endDate: education.endDate,
            gpa: education.gpa,
          }
        })
      }
    } catch (error) {
      console.error("Failed to save education:", error);
    }
  };

  const handleSave = async () => {
    setisLoading(true)
    for (let edu of educations) {
      await saveEducation(edu);
    }
    setisEdit(false);
    loadJobata();
    setisLoading(false)
    setIsFading(true);
    setTimeout(() => {
      if ((currentTabIndex + 1) > profileCompletion) {
        updatePercentage(currentTabIndex + 1)
      }
      setCurrentTabIndex(currentTabIndex + 1);
      setIsFading(false);
    }, 200);
  };

  const handleDelete = async (index, educationID) => {
    if (educationID) {
      try {
        await deleteData({ url: 'EducationHistory', id: educationID, userToken });
      } catch (error) {
        console.error("Failed to delete education:", error);
      }
    }
    setEducations(educations.filter((_, i) => i !== index));
  };

  return (
    <div className="h-full">
      <div className="mt-4 bg-white p-6 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.05)]" style={{ minHeight: window.innerHeight - 300 }}>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-blue-700 font-medium md:text-[20px] text-[17px] flex items-center space-x-2 cursor-pointer">
              <img src={"/asset/icon/education_blue.svg"} className="w-[16px] mr-1" />
              <span>{Lang(lang, 'education')}</span>
            </h2>
            <p className="text-[11px] md:text-[14px] font-normal text-[#1E3A8A]">{Lang(lang, 'edu_subtitle')}</p>
          </div>
          {!isEdit && (
            <button
              onClick={() => setisEdit(true)}
              className="flex border border-gray-300 rounded-full px-5 py-2 text-sm text-white bg-red-500 hover:bg-red-600"
            >
              <img src={"/asset/icon/edit.svg"} className="w-[16px] mr-1" />
              Edit
            </button>
          )}
        </div>

        {educations.length === 0 && !isEdit ? <div className="w-full">
          <EmptyAsset title={Lang(lang, 'no_education')} desc={Lang(lang, 'no_education_desc')} />
        </div> : <form className="space-y-6 text-sm">
          {educations.length === 0 && isEdit ? <div className="w-full">
            <EmptyAsset title={Lang(lang, 'no_education')} desc={Lang(lang, 'no_education_desc')} />
            {isEdit && <button
              type="button"
              onClick={handleAdd}
              className="w-full text-blue-600 border border-dashed border-blue-300 rounded-full py-2 text-sm hover:bg-blue-50 mb-4"
            >
              + {Lang(lang, 'add_edu')}
            </button>}
          </div> : educations.map((edu, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-xl p-4 relative"
            >
              {isEdit && (
                <button
                  type="button"
                  onClick={() => handleDelete(index, edu.educationID)}
                  className="absolute top-3 right-3 text-red-500 hover:text-red-700"
                >
                  <img src={"/asset/icon/trash.svg"} className="w-[16px]" />
                </button>
              )}

              <h3 className="text-gray-700 mb-3 text-[#1E40AF] font-bold">
                {Lang(lang, 'education')} {index + 1}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <label className="block font-medium mb-1">{Lang(lang, 'institution')}<span className="text-red-500">*</span></label>
                  <input
                    disabled={!isEdit}
                    type="text"
                    value={edu.institutionName}
                    onChange={(e) =>
                      handleChange(index, "institutionName", e.target.value)
                    }
                    className={(isEdit ? 'bg-white' : 'bg-gray-100') + " w-full rounded-full border border-gray-200 px-4 py-2 h-[60px]"}
                    placeholder="Institution"
                  />
                </div>

                <div>
                  <label className="block font-medium mb-1">{Lang(lang, 'degree')}<span className="text-red-500">*</span></label>
                  <div className="relative w-full">
                    <select
                      value={edu.educationLevel}
                      disabled={!isEdit}
                      onChange={(e) =>
                        handleChange(index, "educationLevel", e.target.value)
                      }
                      className={(isEdit ? 'bg-white' : 'bg-gray-100') + " appearance-none w-full rounded-full border border-gray-200 px-4 py-2 h-[60px]"}
                    >
                      <option value="" disabled>
                        -- Pendidikan --
                      </option>
                      <option value="S3">S3</option>
                      <option value="S2">S2</option>
                      <option value="S1">S1</option>
                      <option value="D3">D3</option>
                      <option value="D1">D1</option>
                      <option value="SMA">SMA</option>
                      <option value="SMP">SMP</option>
                      <option value="SD">SD</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                      ▼
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">{Lang(lang, 'major')}<span className="text-red-500">*</span></label>
                  <input
                    disabled={!isEdit}
                    type="text"
                    value={edu.major}
                    onChange={(e) => handleChange(index, "major", e.target.value)}
                    className={(isEdit ? 'bg-white' : 'bg-gray-100') + " w-full rounded-full border border-gray-200 px-4 py-2 h-[60px]"}
                    placeholder="Major"
                  />
                </div>

                <div>
                  <label className="block font-medium mb-1">{Lang(lang, 'start_date')}<span className="text-red-500">*</span></label>
                  <input
                    disabled={!isEdit}
                    type="month"
                    value={edu.startDate ? `${edu.startDate}`.split("T")[0].slice(0, 7) : ""}
                    onChange={(e) => handleChange(index, "startDate", e.target.value)}
                    className={(isEdit ? 'bg-white' : 'bg-gray-100') + " w-full rounded-full border border-gray-200 px-4 py-2 h-[60px]"}
                  />
                </div>

                <div>
                  <label className="block font-medium mb-1">{Lang(lang, 'end_date')}</label>
                  <input
                    disabled={!isEdit}
                    type="month"
                    value={edu.endDate ? `${edu.endDate}`.split("T")[0].slice(0, 7) : ""}
                    onChange={(e) => handleChange(index, "endDate", e.target.value)}
                    className={(isEdit ? 'bg-white' : 'bg-gray-100') + " w-full rounded-full border border-gray-200 px-4 py-2 h-[60px]"}
                  />
                </div>

                <div>
                  <label className="block font-medium mb-1">{Lang(lang, 'gpa')}<span className="text-red-500">*</span></label>
                  <input
                    disabled={!isEdit}
                    type="number"
                    value={edu.gpa}
                    onChange={(e) => handleChange(index, "gpa", e.target.value)}
                    className={(isEdit ? 'bg-white' : 'bg-gray-100') + " w-full rounded-full border border-gray-200 px-4 py-2 h-[60px]"}
                    placeholder="GPA"
                  />
                </div>
              </div>
            </div>
          ))}

          {isEdit && educations.length !== 0 && (
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={handleAdd}
                className="w-full text-blue-600 border border-dashed border-blue-300 rounded-full py-2 text-sm hover:bg-blue-50 mb-4"
              >
                + {Lang(lang, 'add_edu')}
              </button>
            </div>
          )}
        </form>}


        {!(isFormReady()) && <div className="flex justify-end w-full">
          <p className="text-red-500 text-xs mt-1 mr-0">{Lang(lang, 'required_error')}</p>
        </div>}

        {(isFormempty()) && <div className="flex justify-end w-full">
          <p className="text-red-500 text-xs mt-1 mr-0">{Lang(lang, 'required_form')}</p>
        </div>}


      </div>
      <div className="flex space-x-2 mt-3 justify-end w-full bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] p-3 rounded-xl sticky bottom-3">
        <button
          type="button"
          className="border border-gray-300 rounded-full px-4 py-1 text-sm hover:bg-gray-50"
          onClick={() => {
            setIsFading(true);
            setTimeout(() => {
              setCurrentTabIndex(currentTabIndex - 1);
              setIsFading(false);
            }, 200);
          }}
        >
          {Lang(lang, 'previous')}
        </button>

        <button
          type="button"
          className={
            "rounded-full px-4 py-1 text-sm " +
            (isEdit && isFormReady() && !isFormempty()
              ? "text-white bg-red-500 hover:bg-red-600"
              : "border border-red-500 hover:bg-gray-50 text-red-500")
          }
          onClick={async () => {
            if (isFormReady() && !isFormempty()) {
              if (isEdit && !isLoading) {
                await handleSave();
              } else {
                setIsFading(true);
                setTimeout(() => {
                  if ((currentTabIndex + 1) > profileCompletion) {
                    updatePercentage(currentTabIndex + 1)
                  }
                  setCurrentTabIndex(currentTabIndex + 1);
                  setIsFading(false);
                }, 200);
              }
            }
          }}
        >
          {isLoading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" /> : isEdit ? Lang(lang, 'save_and_next') : `${Lang(lang, 'next')} ${currentTabIndex + 1}/10`}
        </button>
      </div>
    </div>
  );
};

export default EducationForm;
